import { findCar, formatDuration, isCaptureLive, isInPits } from '@racedash/shared'
import { formatClock } from '@racedash/shared'
import { Datum, NoValue, Tile } from '../Tile'
import type { WidgetProps } from './props'

/**
 * How long since the car last crossed the line.
 *
 * A number that is boring right up until it is the only thing that matters. A
 * lap time tells you the car was fine when it last came past; this tells you
 * whether it has come past. Something has gone wrong long before anybody has
 * been told, and the first evidence is always a car that is late.
 *
 * Taken from the laps rather than the standings, because the standings document
 * is coalesced — written at most every few seconds whether or not anything
 * happened — so its own write time says nothing about when this car passed.
 * `completedAt` is the lap end the vendor reported, which is the crossing
 * itself.
 *
 * Deliberately silent about what a long gap means. It could be a stop, a spin,
 * a garage, or a red flag, and a tile that guessed would be wrong at exactly
 * the moment somebody believed it.
 *
 * The one case it is not silent about is the one the timing system has already
 * answered: a car whose state says it is in the pits. See below.
 */
export default function SinceLastLap({ car, data, now }: WidgetProps<'sinceLastLap'>) {
  const last = data.laps
    .filter((l) => l.car === car && l.completedAt)
    .reduce<Date | null>((latest, l) => {
      const at = l.completedAt?.toDate() ?? null
      if (!at) return latest
      return latest == null || at > latest ? at : latest
    }, null)

  if (!car || !last) {
    return (
      <Tile label="Since last lap">
        <NoValue as="time" />
      </Tile>
    )
  }

  /*
   * Measured to the dashboard's own "now", which stops when the session does.
   *
   * Not the viewer's clock: the correction matters here more than anywhere,
   * because the whole value of this tile is a few seconds of difference. And
   * not a clock that keeps running after the flag — every elapsed figure would
   * otherwise carry on climbing, and a car that retired in May would read as
   * having been away for three months.
   */
  const at = data.asAt(now)
  const sinceMs = Math.max(0, at.getTime() - last.getTime())

  /*
   * A car in the pits is not a car nobody can find.
   *
   * The figure keeps counting, because it stays true — a car sitting in its box
   * genuinely has not crossed the line, and during a qualifying session how
   * long it has been away is worth reading. What it must not do is go red. The
   * colour on this tile means "something has happened that you have not been
   * told about", and a car whose own state says IN has been told about: the
   * timekeeper can see it, and so can anybody looking down the pit lane.
   *
   * Warning about it is worse than saying nothing, because a tile that cries
   * wolf through every pit stop is a tile people stop looking at — and the one
   * time it means it is the time they need it.
   */
  const inPits = isInPits(findCar(data.standings, car))

  // Whether this figure is still moving. Asked of asAt rather than of capture,
  // so the tile's colour and its caption cannot disagree with its number: if
  // the clock has stopped, the figure has stopped, and it should not still be
  // presented as something to act on.
  const running =
    isCaptureLive(data.session?.lastSeenAt?.toDate(), now) &&
    data.session?.clock?.running !== false &&
    !data.session?.finishedAt

  /*
   * Two thresholds, and neither is a verdict.
   *
   * A typical lap here is measured rather than assumed — a two-minute circuit
   * and a six-minute one have very different ideas of "late" — and the tile
   * only leans on it once a car is well past a lap and a half, which no amount
   * of ordinary traffic explains.
   */
  const typical = typicalLapMs(data.laps, car)
  // Only while it is still a question. On a finished session the figure is
  // history, and a tile left glowing red about a car that stopped hours ago is
  // an alarm nobody can act on — which teaches people to ignore the colour.
  const tone =
    inPits || !running || typical == null
      ? undefined
      : sinceMs > typical * 2.5
        ? 'red'
        : sinceMs > typical * 1.5
          ? 'yellow'
          : undefined

  return (
    <Tile label="Since last lap" tone={tone}>
      <Datum
        value={formatDuration(sinceMs)}
        as="time"
        sub={
          inPits
            ? // Said rather than left to be inferred from a tile that has quietly
              // stopped changing colour.
              'in the pits'
            : running
              ? formatClock(last, { seconds: true })
              : 'before the end'
        }
      />
    </Tile>
  )
}

/**
 * What a lap normally takes for this car, from what it has actually done.
 *
 * The median of the recent green laps rather than the mean, because the window
 * this reads will usually contain an in-lap or an out-lap and those are three
 * times a racing lap — enough to drag a mean somewhere no lap ever was.
 */
function typicalLapMs(laps: readonly { car: string; lapTimeMs?: number | null; inLap?: boolean; outLap?: boolean }[], car: string): number | null {
  const times = laps
    .filter((l) => l.car === car && !l.inLap && !l.outLap)
    .map((l) => l.lapTimeMs)
    .filter((ms): ms is number => ms != null && ms > 0)
    .sort((a, b) => a - b)
  if (times.length === 0) return null
  return times[Math.floor(times.length / 2)] ?? null
}
